import { useEffect, useState, useCallback } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import { Btn, Input, Select } from "@/shared/components/UI";
import FileUpload from "@/shared/components/FileUpload";
import {
  Ruler, Plus, CheckCircle2, AlertTriangle, Upload, FileText, Layers, Users,
  Lock, Pencil, User, History, MessageSquare, Sparkles, ChevronDown, Mail,
} from "lucide-react";
import { toast } from "sonner";

/* Msg 901 — per-line scope panel, redesigned around a clear HIERARCHY:
   ┌ OVERALL SCOPE (parent heading) — material, measured-vs-scope, breach ┐
   │   └ WALL / GLASS sub-cards (nested, left-bordered)                   │
   The CSM sub-classifies a line into walls/glasses (each a CHILD BOQ line
   with its own pipeline). Once set the count is FIXED — adjusting needs a
   reason. The PSM measures each wall (L×B → area, auto-converted to the
   line's unit so sqm/sqft stay in sync). A designer is assigned PER WALL with
   a written CSM brief; pre-press is assigned per wall once design is approved. */

const BAR = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-rose-500", na: "bg-zinc-300" };
const TXT = { green: "text-emerald-700", amber: "text-amber-700", red: "text-rose-700", na: "text-zinc-500" };

const STAGES_FULL = [
  ["design_approval", "Design"], ["client_design_approval", "Client OK"], ["site_audit", "Site Measure"],
  ["pre_press", "Pre-Press"], ["files_received", "Files Ready"], ["production_progress", "Production"],
  ["delivery_progress", "Delivery"], ["installation_progress", "Installation"],
];
// Outsourced scope lines (and their walls) end at the production file ("Files
// Ready") — no in-house production / delivery / installation (Msg 916).
const STAGES_OUTSOURCED = STAGES_FULL.slice(0, 5);
const _done = (v) => ["completed", "done", "na", "approved"].includes((v || "").toLowerCase());
const _prog = (v) => ["in_progress", "modify", "sent", "pending_review"].includes((v || "").toLowerCase());

const EYEBROW = "font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-400";

// Group walls by their sub-scope group (created in Sub-scope by the CSM).
function groupWalls(walls) {
  const m = new Map();
  for (const w of walls) {
    const g = w.group || "";
    if (!m.has(g)) m.set(g, []);
    m.get(g).push(w);
  }
  return [...m.entries()]
    .sort((a, b) => (a[0] === "" ? 1 : b[0] === "" ? -1 : a[0].localeCompare(b[0])))
    .map(([name, ws]) => ({ name, walls: ws }));
}

export default function BoqScopePanel({
  line, code, me, canSeeMoney = false, onChange,
  designerOptions = [], canAssign = false, onAssignDesigner,
  prepressOptions = [], canAssignPrepress = false, onAssignPrepress,
}) {
  const [data, setData] = useState(null);
  const role = me?.role;
  const canMeasure = ["admin", "management", "psm"].includes(role);
  const canPO = ["admin", "management", "scope_user"].includes(role);
  // Msg 962 — the CSM can attach the email confirmation / PO DOCUMENT for a
  // variation from the BOQ portal (no commercial value — Scope User approves).
  const canVarDoc = canPO || role === "csm";

  const load = useCallback(() => {
    api.get(`/projects/${code}/boq/${line.id}/walls`)
      .then((r) => setData(r.data)).catch(() => setData({ walls: [] }));
  }, [code, line.id]);
  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChange && onChange(); };
  const isVariation = line.is_variation;
  const isBreached = !isVariation && line.scope_status === "red" && !line.breach_resolved;
  const walls = data?.walls || [];

  return (
    <div className="px-4 sm:px-6 py-5 bg-zinc-50 border-y border-zinc-200" data-testid={`boq-scope-panel-${line.id}`}>
      {isVariation && line.variation_status !== "approved" && (
        <VariationBox line={line} code={code} canPO={canPO} canDoc={canVarDoc} onChange={refresh} />
      )}
      {isBreached && <AmendedPOBox line={line} code={code} canPO={canPO} onChange={refresh} />}

      {/* ── PARENT: overall scope ─────────────────────────────────────── */}
      <div className="bg-white border border-zinc-200 rounded-xl shadow-sm p-5 sm:p-6"
           data-testid={`boq-parent-scope-${line.id}`}>
        <ScopeHeader line={line} data={data} canSeeMoney={canSeeMoney} />

        {/* Wall structure, measurement, designer & pre-press assignment all live
            in the dedicated portals (Sub-scope / Pre-Audit / Pre-Press board).
            Here we only surface progress. */}

        {/* ── CHILDREN: walls / glasses, grouped by sub-scope ─────────── */}
        <div className="mt-6">
          <div className={`${EYEBROW} flex items-center gap-1.5`}>
            <Layers className="w-3.5 h-3.5" strokeWidth={1.5} />
            Walls / Glasses {walls.length ? `· ${walls.length}` : ""}
          </div>
          {walls.length === 0 ? (
            <div className="mt-2 px-4 py-5 text-xs text-zinc-400 italic rounded-lg bg-zinc-50 border border-dashed border-zinc-200">
              Walls &amp; glass panels are created and grouped in <b className="text-zinc-500">Sub-scope</b> by the CSM. They&apos;ll appear here with their pipeline progress.
            </div>
          ) : (
            <div className="mt-3 space-y-5">
              {groupWalls(walls).map((grp) => (
                <WallGroupBlock key={grp.name || "__ungrouped__"} grp={grp} data={data} canSeeMoney={canSeeMoney} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ScopeHeader({ line, data, canSeeMoney }) {
  const status = data?.scope_status || line.scope_status || "na";
  const pct = Math.min(100, data?.scope_pct || 0);
  const over = (data?.scope_variance || 0) > 0;
  const unit = data?.unit || line.unit || "";
  const isSqm = data?.is_sqm;
  const measured = data?.measured_total ?? line.actual_measured_total ?? 0;
  const ceiling = data?.boq_quantity ?? line.quantity_required ?? 0;
  return (
    <div data-testid={`boq-progress-bar-${line.id}`}>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
        <div className="min-w-0">
          <div className={EYEBROW}>Overall scope</div>
          <h3 className="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-900 mt-0.5 truncate">
            {line.material_name || "Scope line"}
          </h3>
          <div className="text-xs text-zinc-500 mt-1">
            Approved <b className="text-zinc-700">{ceiling} {unit}</b>
            {isSqm && <span className="text-zinc-400"> · ≈ {data?.boq_quantity_sqft} sqft</span>}
            {canSeeMoney && (line.rate || data?.rate) ? (
              <span className="text-emerald-700"> · ₹{Number(line.rate || data?.rate || 0).toLocaleString("en-IN")}/{unit}</span>
            ) : null}
          </div>
        </div>
        {status === "red" ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-rose-50 text-rose-600 border border-rose-200 self-start whitespace-nowrap"
                data-testid={`scope-breach-badge-${line.id}`}>
            <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} /> Scope exceeded
          </span>
        ) : status !== "na" && (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border self-start whitespace-nowrap
            ${status === "amber" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> {status === "amber" ? "Approaching scope" : "Within scope"}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="font-mono uppercase tracking-widest text-zinc-400 flex items-center gap-1.5">
          <Ruler className="w-3 h-3" strokeWidth={1.5} /> Measured vs scope
        </span>
        <span className={`font-semibold tabular-nums ${TXT[status] || "text-zinc-600"}`}
              data-testid={`scope-status-${line.id}`}>
          {measured} / {ceiling} {unit}
          {isSqm && <span className="text-zinc-400"> (≈ {data?.measured_total_sqft} sqft)</span>}
          {over ? <span className="ml-1">· +{data?.scope_variance} over</span>
                : status !== "na" && <span className="ml-1 text-zinc-400">· {data?.wall_measured_count}/{data?.wall_count} measured</span>}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-zinc-100 overflow-hidden">
        <div className={`h-full transition-all duration-500 ${BAR[status] || "bg-zinc-300"}`} style={{ width: `${pct}%` }} />
      </div>
      {status === "red" && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-rose-700 font-medium">
          <AlertTriangle className="w-3.5 h-3.5" strokeWidth={1.5} /> Scope exceeded — Management & Scope User notified for an Amended PO.
        </div>
      )}
      {isSqm && (
        <div className="mt-2 text-[11px] text-zinc-400 flex items-center gap-1">
          <Sparkles className="w-3 h-3" strokeWidth={1.5} /> Scope is in <b className="text-zinc-500">sqm</b>; on-site measurements (sqft) are auto-converted so totals stay in sync.
        </div>
      )}
    </div>
  );
}

// AddWalls / AdjustWalls removed (Msg 931): wall sub-classification now lives
// exclusively in the CSM Sub-scope workspace; the BOQ panel is read-only here.



function BulkAssignPrepress({ code, bid, prepressOptions, eligible, onDone }) {
  const [pp, setPp] = useState("");
  const [saving, setSaving] = useState(false);
  async function assignAll() {
    setSaving(true);
    try {
      const r = await api.post(`/projects/${code}/boq/${bid}/walls/assign-prepress`, { prepress_id: pp });
      toast.success(pp ? `Pre-press assigned to ${r.data.updated} wall(s).` : "Cleared pre-press on all walls.");
      onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }
  return (
    <div className="mt-3 flex flex-wrap items-end gap-2 p-3 rounded-lg bg-sky-50 border border-sky-200">
      <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-sky-700">
        <Users className="w-3.5 h-3.5" strokeWidth={1.5} /> Pre-press → approved walls ({eligible} ready)
      </div>
      <div className="w-44">
        <Select value={pp} onChange={(e) => setPp(e.target.value)} data-testid={`bulk-prepress-select-${bid}`}>
          <option value="">— pick pre-press —</option>
          {prepressOptions.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </Select>
      </div>
      <Btn variant="secondary" onClick={assignAll} disabled={saving || !pp} data-testid={`bulk-prepress-btn-${bid}`}
           className="border-sky-300 text-sky-700 hover:bg-sky-100">Assign all</Btn>
    </div>
  );
}

function WallProgress({ w }) {
  const stages = w.production_type === "outsourced" ? STAGES_OUTSOURCED : STAGES_FULL;
  return (
    <div className="grid gap-1.5" data-testid={`wall-progress-${w.id}`}
         style={{ gridTemplateColumns: `repeat(${stages.length}, minmax(0, 1fr))` }}>
      {stages.map(([k, lbl]) => {
        const v = w[k];
        const cls = _done(v) ? "bg-emerald-500" : _prog(v) ? "bg-amber-400" : "bg-zinc-200";
        return (
          <div key={k} className="flex flex-col items-center gap-1 min-w-0" title={`${lbl}: ${v || "pending"}`}
               data-testid={`boq-wall-pipeline-step-${w.id}-${k}`}>
            <div className={`h-1.5 w-full rounded-sm transition-colors ${cls}`} />
            <span className="text-[8px] font-mono uppercase tracking-tight text-zinc-400 text-center leading-tight w-full">{lbl}</span>
          </div>
        );
      })}
    </div>
  );
}

// Collapsible sub-scope group block (e.g. "Reception walls") inside a BOQ line. (Msg 936)
function WallGroupBlock({ grp, data, canSeeMoney }) {
  const [open, setOpen] = useState(true);
  return (
    <div data-testid={`boq-wall-group-${grp.name || "ungrouped"}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
              data-testid={`boq-wall-group-toggle-${grp.name || "ungrouped"}`}
              className="w-full flex items-center gap-1.5 mb-2 text-[11px] font-mono uppercase tracking-widest text-orange-600 hover:text-orange-700 transition-colors">
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} strokeWidth={1.5} />
        <Layers className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} /> {grp.name || "Ungrouped"}
        <span className="text-zinc-300">· {grp.walls.length}</span>
      </button>
      {open && (
        <div className="ml-1 sm:ml-3 pl-4 sm:pl-6 border-l-2 border-zinc-200 space-y-3">
          {grp.walls.map((w) => (
            <WallCard key={w.id} w={w} isSqm={data.is_sqm} unit={data.unit} canSeeMoney={canSeeMoney} />
          ))}
        </div>
      )}
    </div>
  );
}

/* Per-wall designer assignment + CSM brief (what's required for this wall). */
function WallDesigner({ w, options, canAssign, onAssign }) {
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  const [pick, setPick] = useState("");
  const [brief, setBrief] = useState("");
  const name = w.designer_name || options.find((o) => o.id === w.designer_id)?.name || "";

  // Read-only view (no permission)
  if (!canAssign) {
    return name ? (
      <div className="min-w-0">
        <span className="inline-flex items-center gap-1 text-xs text-zinc-700" data-testid={`wall-designer-name-${w.id}`}>
          <User className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5} /> {name}
        </span>
        {w.designer_brief && (
          <div className="mt-1 text-[11px] text-zinc-500 line-clamp-2" title={w.designer_brief}>“{w.designer_brief}”</div>
        )}
      </div>
    ) : <span className="text-[11px] text-zinc-400">No designer yet</span>;
  }

  // First assignment — pick designer + write a brief
  if (!w.designer_id && !editing) {
    return (
      <div className="space-y-1.5" data-testid={`wall-designer-assign-${w.id}`}>
        <Select value={pick} onChange={(e) => setPick(e.target.value)}
                className="!h-8 text-xs" data-testid={`boq-wall-designer-select-${w.id}`}>
          <option value="">Assign designer…</option>
          {options.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </Select>
        {pick && (
          <>
            <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={2}
                      placeholder="Brief for the designer — what's required on this wall?"
                      className="w-full text-xs rounded-md border border-zinc-300 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500"
                      data-testid={`wall-designer-brief-${w.id}`} />
            <Btn onClick={() => { onAssign(w.id, pick, "", brief.trim()); setPick(""); setBrief(""); }}
                 className="bg-amber-500 hover:bg-amber-600 !h-8 text-xs w-full justify-center"
                 data-testid={`wall-designer-assign-save-${w.id}`}>
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Assign with brief
            </Btn>
          </>
        )}
      </div>
    );
  }

  // Assigned — show name + brief + change link
  if (w.designer_id && !editing) {
    return (
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center gap-1 text-xs font-medium text-zinc-800 truncate" data-testid={`wall-designer-name-${w.id}`}>
            <User className="w-3.5 h-3.5 text-zinc-400" strokeWidth={1.5} /> {name}
          </span>
          <button onClick={() => { setReason(""); setPick(w.designer_id); setBrief(w.designer_brief || ""); setEditing(true); }}
                  data-testid={`wall-designer-change-${w.id}`}
                  className="text-[10px] font-mono uppercase tracking-wider text-amber-700 hover:underline">change</button>
        </div>
        {w.designer_brief && (
          <div className="mt-1 text-[11px] text-zinc-500 flex items-start gap-1" data-testid={`wall-designer-brief-text-${w.id}`}>
            <MessageSquare className="w-3 h-3 mt-0.5 text-zinc-400 flex-shrink-0" strokeWidth={1.5} />
            <span className="line-clamp-2" title={w.designer_brief}>{w.designer_brief}</span>
          </div>
        )}
        {w.designer_last_reason && (
          <div className="text-[10px] text-zinc-400 mt-0.5 truncate" title={w.designer_last_reason}
               data-testid={`wall-designer-reason-${w.id}`}>↳ {w.designer_last_reason}</div>
        )}
      </div>
    );
  }

  // Change flow — reason required to switch designer; brief editable
  const changed = pick !== (w.designer_id || "");
  const ready = !changed || reason.trim().length > 0;
  return (
    <div className="space-y-1.5" data-testid={`wall-designer-edit-${w.id}`}>
      <Select value={pick} onChange={(e) => setPick(e.target.value)} className="!h-8 text-xs"
              data-testid={`wall-designer-new-${w.id}`}>
        <option value="">Pick designer…</option>
        {options.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
      </Select>
      {changed && (
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for change *"
               className="!h-8 text-xs" data-testid={`wall-designer-reason-input-${w.id}`} />
      )}
      <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={2}
                placeholder="Brief for the designer"
                className="w-full text-xs rounded-md border border-zinc-300 px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500"
                data-testid={`wall-designer-brief-edit-${w.id}`} />
      <div className="flex gap-1.5">
        <Btn onClick={() => { onAssign(w.id, pick, reason.trim(), brief.trim()); setEditing(false); }}
             disabled={!pick || !ready} className="bg-amber-500 hover:bg-amber-600 !h-8 text-xs"
             data-testid={`wall-designer-save-${w.id}`}>Save</Btn>
        <button onClick={() => setEditing(false)} className="text-[10px] text-zinc-400 hover:text-zinc-600">cancel</button>
      </div>
    </div>
  );
}

// Read-only wall row for the BOQ panel — shows the wall's group, measured value,
// designer (name only) and its pipeline progress. All editing (measure, assign
// designer/pre-press, rename, sub-classify) lives in the dedicated portals. (Msg 933)
function WallCard({ w, isSqm, unit, canSeeMoney }) {
  const designer = w.designer_name || "";
  return (
    <div className="bg-zinc-50/80 border border-zinc-200/70 rounded-lg p-4 sm:p-5"
         data-testid={`boq-wall-child-card-${w.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <h4 className="text-base font-medium tracking-tight text-zinc-900" data-testid={`wall-label-${w.id}`}>{w.label}</h4>
          {w.group && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider bg-orange-50 text-orange-600 border border-orange-100" data-testid={`wall-group-badge-${w.id}`}>
              <Layers className="w-3 h-3" strokeWidth={1.5} /> {w.group}
            </span>
          )}
        </div>
        {w.wall_measured ? (
          <div className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-emerald-700">
            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> {w.total_quantity} {unit}
            {isSqm && w.total_quantity_sqft ? <span className="text-zinc-400">≈ {w.total_quantity_sqft} sqft</span> : null}
            {canSeeMoney && w.amount ? <span className="text-zinc-400">· ₹{Number(w.amount).toLocaleString("en-IN")}</span> : null}
          </div>
        ) : <div className="mt-0.5 text-xs text-amber-600">Pending site measurement</div>}
        {designer && (
          <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-zinc-500" data-testid={`wall-designer-name-${w.id}`}>
            <User className="w-3 h-3 text-zinc-400" strokeWidth={1.5} /> {designer}
          </div>
        )}
      </div>
      {/* Pipeline tracker — the progress/stage of this wall */}
      <div className="mt-3 pt-3 border-t border-zinc-200/70">
        <WallProgress w={w} />
      </div>
    </div>
  );
}

function LabeledInput({ label, v, set, tid }) {
  return (
    <div>
      <label className={EYEBROW}>{label}</label>
      <Input type="number" step="0.01" value={v} onChange={(e) => set(e.target.value)} className="mt-1"
             data-testid={tid} />
    </div>
  );
}

/* ── PO upload cards (Scope User) — variation + breach ───────────────── */
function PODropzone({ value, onChange, label, accent = "amber", testid }) {
  const ring = accent === "rose" ? "hover:border-rose-400 hover:bg-rose-50/50" : "hover:border-amber-400 hover:bg-amber-50/50";
  return (
    <div className={`rounded-lg border-2 border-dashed border-zinc-200 p-1 transition-colors ${ring}`} data-testid={testid}>
      <FileUpload value={value} onChange={onChange} label={label}
                  accept=".pdf,.png,.jpg,.jpeg,.xlsx,.docx" testid={`${testid}-input`} />
    </div>
  );
}

function VariationBox({ line, code, canPO, canDoc, onChange }) {
  const [url, setUrl] = useState(""); const [name, setName] = useState(""); const [ref, setRef] = useState("");
  const [amended, setAmended] = useState(false); const [rate, setRate] = useState("");
  const [totalQty, setTotalQty] = useState(line.quantity_required || ""); const [saving, setSaving] = useState(false);
  const [addingMore, setAddingMore] = useState(false);
  const status = line.variation_status;
  const amount = (parseFloat(rate || "0") || 0) * (parseFloat(totalQty || "0") || 0);
  const emailList = Array.isArray(line.variation_emails) && line.variation_emails.length
    ? line.variation_emails
    : (line.variation_email_url ? [{ url: line.variation_email_url, name: line.variation_email_name }] : []);
  const emailConfirmed = status !== "po_uploaded" && emailList.length > 0;
  const showUploader = canDoc && status !== "po_uploaded" && (canPO || !emailConfirmed || addingMore);
  async function uploadPO(docType = "po") {
    if (!url) { toast.error(docType === "email" ? "Attach the email confirmation" : "Attach the PO file"); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${code}/boq/${line.id}/variation-po`,
                     { file_url: url, file_name: name, po_ref: ref, amended, doc_type: docType,
                       rate: parseFloat(rate || "0") || 0, total_quantity: parseFloat(totalQty || "0") || 0 });
      toast.success(docType === "email" ? "Email confirmation saved — awaiting the actual PO." : "PO saved.");
      setUrl(""); setName(""); setRef(""); setAddingMore(false); onChange();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setSaving(false); }
  }
  async function approve() {
    setSaving(true);
    try { await api.post(`/projects/${code}/boq/${line.id}/variation-approve`); toast.success("Variation approved."); onChange(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setSaving(false); }
  }
  return (
    <div className="mb-4 bg-white border border-violet-200 rounded-xl shadow-sm p-5 sm:p-6" data-testid={`variation-box-${line.id}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-violet-600">Variation</span>
        <span className="text-[11px] text-violet-500">· {status === "po_uploaded" ? "Awaiting approval" : emailConfirmed ? "Email confirmed · awaiting PO" : "Awaiting New / Amended PO"}</span>
      </div>
      {line.variation_reason && <div className="text-sm text-zinc-700 mb-3">{line.variation_reason}</div>}
      {emailConfirmed && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3"
             data-testid={`variation-email-banner-${line.id}`}>
          <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span>{emailList.length === 1 ? "Email confirmation on file" : `${emailList.length} email confirmations on file`} — upload the actual PO when it arrives.</span>
          {emailList.map((e, i) => (
            <a key={i} href={e.url} target="_blank" rel="noreferrer" className="underline font-medium" data-testid={`variation-email-link-${line.id}-${i}`}>
              {e.name || `Email ${i + 1}`}
            </a>
          ))}
        </div>
      )}
      {line.variation_po_url && (
        <div className="flex items-center gap-1.5 text-xs text-violet-700 mb-3">
          <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
          <a href={line.variation_po_url} target="_blank" rel="noreferrer" className="underline">
            {line.variation_amended ? "Amended PO" : "New PO"} {line.variation_po_ref ? `· ${line.variation_po_ref}` : ""}
          </a>
        </div>
      )}
      {showUploader && (
        <div className="space-y-4">
          <PODropzone value={url} onChange={(x) => { setUrl(x); setName(x ? (x.split("/").pop() || "po") : ""); }}
                      label={canPO ? "Attach PO / email confirmation" : "Attach the client's email confirmation"} testid={`po-upload-dropzone-${line.id}`} />
          {canPO && (
            <div className="grid grid-cols-2 gap-4 max-w-md">
              <div><label className={EYEBROW}>Total Qty</label>
                <Input type="number" step="0.01" value={totalQty} onChange={(e) => setTotalQty(e.target.value)} className="mt-1" data-testid={`po-qty-input-${line.id}`} /></div>
              <div><label className={EYEBROW}>Rate (₹)</label>
                <Input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className="mt-1" data-testid={`po-rate-input-${line.id}`} /></div>
              <div className="col-span-2 bg-zinc-50 border border-zinc-200 rounded-md p-3 flex justify-between items-center" data-testid={`po-amount-display-${line.id}`}>
                <span className="text-xs text-zinc-500 uppercase tracking-wider font-mono">Amount</span>
                <span className="text-lg font-semibold tracking-tight text-zinc-900">₹{amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
              </div>
            </div>
          )}
          {!canPO && (
            <p className="text-[11px] text-zinc-400 -mt-1">
              You can attach the client's <b>email confirmation</b> here. The Scope / Sales user uploads the actual PO &amp; sets the commercial value.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="PO reference #" className="max-w-[180px]" data-testid={`po-ref-number-input-${line.id}`} />
            {canPO && (
              <label className="flex items-center gap-1.5 text-xs text-violet-700">
                <input type="checkbox" checked={amended} onChange={(e) => setAmended(e.target.checked)} data-testid={`variation-amended-${line.id}`} /> Amended PO
              </label>
            )}
            {canPO && (
              <Btn onClick={() => uploadPO("po")} disabled={saving || !url} className="bg-violet-600 hover:bg-violet-700" data-testid={`po-submit-button-${line.id}`}>
                <Upload className="w-4 h-4" strokeWidth={1.5} /> Upload PO
              </Btn>
            )}
            <Btn onClick={() => uploadPO("email")} disabled={saving || !url} className="bg-amber-500 hover:bg-amber-600" data-testid={`variation-email-submit-${line.id}`}>
              <Mail className="w-4 h-4" strokeWidth={1.5} /> Upload email confirmation
            </Btn>
          </div>
        </div>
      )}
      {canDoc && !canPO && emailConfirmed && !addingMore && status !== "po_uploaded" && (
        <Btn onClick={() => setAddingMore(true)} className="bg-amber-500 hover:bg-amber-600" data-testid={`variation-add-email-${line.id}`}>
          <Mail className="w-4 h-4" strokeWidth={1.5} /> Add more emails
        </Btn>
      )}
      {canPO && status === "po_uploaded" && (
        <Btn onClick={approve} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid={`variation-approve-${line.id}`}>
          <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> Approve variation
        </Btn>
      )}
    </div>
  );
}

function AmendedPOBox({ line, code, canPO, onChange }) {
  const [url, setUrl] = useState(""); const [name, setName] = useState(""); const [ref, setRef] = useState("");
  const [newQty, setNewQty] = useState(line.actual_measured_total || line.quantity_required || "");
  const [rate, setRate] = useState(line.rate || line.unit_cost || ""); const [saving, setSaving] = useState(false);
  const amount = (parseFloat(rate || "0") || 0) * (parseFloat(newQty || "0") || 0);
  async function upload() {
    if (!url) { toast.error("Attach the amended PO file"); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${code}/boq/${line.id}/amended-po`,
                     { file_url: url, file_name: name, po_ref: ref, new_quantity: parseFloat(newQty || "0") || 0, rate: parseFloat(rate || "0") || 0 });
      toast.success("Amended PO uploaded — breach resolved."); setUrl(""); setName(""); setRef(""); onChange();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); } finally { setSaving(false); }
  }
  return (
    <div className="mb-4 bg-white border-2 border-rose-200 rounded-xl shadow-sm p-5 sm:p-6" data-testid={`amended-po-box-${line.id}`}>
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-4 h-4 text-rose-500" strokeWidth={1.5} />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-rose-600">Scope exceeded · pending Amended PO</span>
      </div>
      <div className="text-xs text-zinc-600 mb-4">
        Measured <b className="text-rose-700">{line.actual_measured_total}</b> {line.unit} vs approved <b>{line.quantity_required}</b> {line.unit}
        {line.scope_variance != null && <> (+{line.scope_variance} {line.unit} over)</>}.
        {!canPO && " The Scope User will upload an amended PO."}
      </div>
      {canPO && (
        <div className="space-y-4">
          <PODropzone value={url} onChange={(x) => { setUrl(x); setName(x ? (x.split("/").pop() || "po") : ""); }}
                      label="Upload Amended PO" accent="rose" testid={`amended-po-dropzone-${line.id}`} />
          <div className="grid grid-cols-2 gap-4 max-w-md">
            <div><label className={EYEBROW}>New approved qty</label>
              <Input type="number" step="0.01" value={newQty} onChange={(e) => setNewQty(e.target.value)} className="mt-1" data-testid={`amended-po-qty-${line.id}`} /></div>
            <div><label className={EYEBROW}>Rate (₹)</label>
              <Input type="number" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className="mt-1" data-testid={`amended-po-rate-${line.id}`} /></div>
            <div className="col-span-2 bg-rose-50 border border-rose-200 rounded-md p-3 flex justify-between items-center" data-testid={`amended-po-amount-${line.id}`}>
              <span className="text-xs text-rose-500 uppercase tracking-wider font-mono">Revised amount</span>
              <span className="text-lg font-semibold tracking-tight text-rose-700">₹{amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Amended PO reference #" className="max-w-[200px]" data-testid={`amended-po-ref-${line.id}`} />
            <Btn onClick={upload} disabled={saving || !url} className="bg-rose-600 hover:bg-rose-700" data-testid={`amended-po-save-${line.id}`}>
              <Upload className="w-4 h-4" strokeWidth={1.5} /> Upload Amended PO
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
}
